import React, { useEffect, useState } from 'react';
import { ShoppingCartIcon, UserIcon, MenuIcon, XIcon, SearchIcon, ChevronDownIcon } from 'lucide-react';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Home',
    href: '#'
  }, {
    name: 'Shop All',
    href: '#shop-all',
    hasDropdown: true
  }, {
    name: 'Gen 3',
    href: '#gen3',
    highlight: true
  }, {
    name: 'Support',
    href: 'https://www.daedosupport.com/portal/en/kb/tkd-score',
    hasDropdown: true,
    external: true
  }, {
    name: 'Financing',
    href: '#financing'
  }];
  const shopDropdownLinks = [{
    name: 'Daedo PSS Systems',
    href: '#daedo-pss-systems'
  }, {
    name: 'Daedo Products',
    href: '#daedo-products'
  }, {
    name: 'Wacoku Products',
    href: '#wacoku-products'
  }];
  const supportDropdownLinks = [{
    name: 'Thresholds/Hit Levels',
    href: '#support-thresholds'
  }, {
    name: 'Download Scoring Software',
    href: '#support-software'
  }, {
    name: 'Troubleshooting Video',
    href: '#support-troubleshooting'
  }, {
    name: 'E-Foot Usage Recommendations',
    href: '#support-efoot'
  }];
  const getDropdownLinks = (name: string) => {
    if (name === 'Shop All') return shopDropdownLinks;
    if (name === 'Support') return supportDropdownLinks;
    return [];
  };
  const mobileNavLinks = [{
    name: 'Home',
    href: '#'
  }, {
    name: 'Shop All',
    href: '#shop-all'
  }, {
    name: 'Daedo PSS Systems',
    href: '#daedo-pss-systems'
  }, {
    name: 'Daedo Products',
    href: '#daedo-products'
  }, {
    name: 'Wacoku Products',
    href: '#wacoku-products'
  }, {
    name: 'Gen 3',
    href: '#gen3',
    highlight: true
  }, {
    name: 'Support',
    href: 'https://www.daedosupport.com/portal/en/kb/tkd-score',
    external: true
  }, {
    name: 'Thresholds/Hit Levels',
    href: '#support-thresholds',
    indent: true
  }, {
    name: 'Download Scoring Software',
    href: '#support-software',
    indent: true
  }, {
    name: 'Troubleshooting Video',
    href: '#support-troubleshooting',
    indent: true
  }, {
    name: 'E-Foot Usage Recommendations',
    href: '#support-efoot',
    indent: true
  }, {
    name: 'Financing',
    href: '#financing'
  }];
  return <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-white py-4 border-b border-gray-100'}`} data-id="element-100">
      {/* Top utility bar */}
      <div className="bg-gray-900 text-white text-xs py-1.5 px-4 hidden md:block" data-id="element-101">
        <div className="max-w-7xl mx-auto flex justify-between items-center" data-id="element-102">
          <p className="font-medium tracking-wide" data-id="element-103">
            Official Distributor of Daedo Electronic Scoring Equipment
          </p>
          <div className="flex space-x-6" data-id="element-104">
            <a href="#" className="hover:text-blue-300 transition-colors" data-id="element-105">
              Financing Available
            </a>
            <a href="#" className="hover:text-blue-300 transition-colors" data-id="element-106">
              Team Quotes
            </a>
            <a href="#" className="hover:text-blue-300 transition-colors" data-id="element-107">
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-108">
        <div className="flex justify-between items-center h-16" data-id="element-109">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center" data-id="element-110">
            <a href="/" className="flex items-center" data-id="element-111">
              <img src="https://cdn.magicpatterns.com/uploads/m5AwQ7uZJXzvkVwSXn43Dr/logo.png" alt="TKDScore.com Logo" className="h-10 w-auto" data-id="element-112" />
            </a>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex space-x-8" data-id="element-113">
            {navLinks.map(link => <div key={link.name} className="relative" onMouseEnter={() => link.hasDropdown && setOpenDropdown(link.name)} onMouseLeave={() => link.hasDropdown && setOpenDropdown(null)} data-id="element-114">
                <a href={link.href} {...link.external ? {
              target: '_blank',
              rel: 'noopener noreferrer'
            } : {}} className={`text-sm font-bold uppercase tracking-wide transition-colors flex items-center gap-1 ${link.highlight ? 'text-red-600 hover:text-red-700' : 'text-gray-700 hover:text-blue-600'}`} data-id="element-115">
                  {link.name}
                  {link.hasDropdown && <ChevronDownIcon className="w-4 h-4" data-id="element-116" />}
                </a>

                {/* Dropdown Menu */}
                {link.hasDropdown && openDropdown === link.name && <div className="absolute top-full left-0 pt-2 w-64 z-50" data-id="element-117">
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2" data-id="element-118">
                      {getDropdownLinks(link.name).map(dropdownLink => <a key={dropdownLink.name} href={dropdownLink.href} className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors" data-id="element-119">
                          {dropdownLink.name}
                        </a>)}
                    </div>
                  </div>}
              </div>)}
          </nav>

          {/* Icons */}
          <div className="hidden lg:flex items-center space-x-6" data-id="element-120">
            <button className="text-gray-600 hover:text-blue-600 transition-colors" aria-label="Search" data-id="element-121">
              <SearchIcon className="w-5 h-5" data-id="element-122" />
            </button>
            <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-2 text-sm font-semibold" aria-label="Account" data-id="element-123">
              <UserIcon className="w-5 h-5" data-id="element-124" />
              <span data-id="element-125">Account</span>
            </a>
            <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-2 text-sm font-semibold relative" aria-label="Cart" data-id="element-126">
              <ShoppingCartIcon className="w-5 h-5" data-id="element-127" />
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-id="element-128">
                0
              </span>
              <span data-id="element-129">Cart</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center space-x-4" data-id="element-130">
            <a href="#" className="text-gray-600 relative" aria-label="Cart" data-id="element-131">
              <ShoppingCartIcon className="w-6 h-6" data-id="element-132" />
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-id="element-133">
                0
              </span>
            </a>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-600 hover:text-gray-900 focus:outline-none" aria-label="Toggle menu" data-id="element-134">
              {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-135" /> : <MenuIcon className="w-6 h-6" data-id="element-136" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className="lg:hidden bg-white border-t border-gray-100 absolute w-full shadow-lg" data-id="element-137">
          <div className="px-4 pt-2 pb-6 space-y-1" data-id="element-138">
            {mobileNavLinks.map(link => <a key={link.name} href={link.href} {...link.external ? {
          target: '_blank',
          rel: 'noopener noreferrer'
        } : {}} className={`block px-3 py-3 rounded-md text-base font-bold uppercase tracking-wide ${link.highlight ? 'text-red-600 bg-red-50' : link.indent ? 'text-gray-600 hover:bg-gray-50 pl-8 text-sm normal-case font-semibold' : 'text-gray-900 hover:bg-gray-50'}`} data-id="element-139">
                {link.name}
              </a>)}
            <div className="border-t border-gray-100 mt-4 pt-4 space-y-2" data-id="element-140">
              <a href="#" className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md" data-id="element-141">
                Financing
              </a>
              <a href="#" className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md" data-id="element-142">
                Account / Login
              </a>
            </div>
          </div>
        </div>}
    </header>;
}